<template>
  <div class="page page-data-management">
    <div class="page-toolbar bg-white shadow-sm p-3">
      <breadcrumb />
      <div>
        <entity-views-dropdown
          v-if="!loading"
          :views="table.views"
          @view-choose="chooseView"
        />
      </div>
      <div class="actions_wrapper">
        <b-button
          v-if="!isLoanCalculatorOutput && canAddRow(table.schema)"
          id="addNewRow"
          size="sm"
          :disabled="readOnly"
          @click="addNewRow"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
          Add new row
        </b-button>
        <b-button
          id="showAppliedFilters"
          :variant="isEditViewButtonHighlighted ? 'warning' : 'secondary'"
          size="sm"
          @click="showFilters = !showFilters"
        >
          <font-awesome-icon :icon="['fas', 'sort-amount-up-alt']" />
          Edit View {{ filtersApplied || sorting.length || groupBy || subGroupBy ? `(${filtersAppliedCount})` : '' }}
        </b-button>
        <toggle-columns-button
          v-if="!loading"
          :group-by="groupBy"
          :sub-group-by="subGroupBy"
          :fields="tableAllFields"
          :toggle-columns="toggleColumns"
          @change="onToggleColumnsChanged"
        />

        <b-dropdown
          v-if="!loading"
          id="tableActions"
          size="sm"
          variant="info"
          toggle-class="text-decoration-none"
          no-caret
        >
          <template #button-content>
            <font-awesome-icon :icon="['fas', 'cog']" />
            Actions
          </template>
          <b-dropdown-item
            v-if="table_canEdit"
            id="importFile"
            @click="table_showImport('import-file')"
          >
            <font-awesome-icon
              :icon="['fas', 'cloud-upload-alt']"
              class="mr-2"
            />
            Import File
          </b-dropdown-item>
          <b-dropdown-item
            v-if="table_canEdit"
            id="uploadData"
            @click="table_showImport('upload-data')"
          >
            <font-awesome-icon
              :icon="['fas', 'paste']"
              class="mr-2"
              style="width: 1.25em"
            />
            Upload Data
          </b-dropdown-item>
          <b-dropdown-item
            id="exportData"
            @click="table_exportData"
          >
            <font-awesome-icon
              :icon="['fas', 'cloud-download-alt']"
              class="mr-2"
            />
            Export
          </b-dropdown-item>
          <b-dropdown-item
            id="history"
            @click="$router.push({name: 'dataManagementHistoryTableView'})"
          >
            <font-awesome-icon
              :icon="['fas', 'history']"
              class="mr-2"
              style="width: 1.25em"
            />
            History
          </b-dropdown-item>
          <b-dropdown-item
            v-if="table_canEdit || table_canEditField"
            id="bulkEdit"
            :disabled="!table_enabledBulkActions"
            @click="table_bulkEditRows"
          >
            <font-awesome-icon
              :icon="['fas', 'edit']"
              class="mr-2"
              style="width: 1.25em"
            />
            Bulk Edit
          </b-dropdown-item>
          <b-dropdown-item
            v-if="table_canDelete"
            id="bulkDelete"
            :disabled="!table_enabledBulkActions"
            @click="table_onBulkDelete"
          >
            <font-awesome-icon
              :icon="['fas', 'trash']"
              class="mr-2"
              style="width: 1.25em"
            />
            Bulk Delete
          </b-dropdown-item>
          <!-- Add upload history to actions dropdown -->
          <b-dropdown-item
            id="uploadHistory"
            @click="showUploadHistoryModal = true"
          >
            <font-awesome-icon
              :icon="['fas', 'history']"
              class="mr-2"
              style="width: 1.25em"
            />
            Upload History
          </b-dropdown-item>
        </b-dropdown>
        <b-button
          v-if="isLoanCalculatorOutput"
          size="sm"
          variant="outline-success"
          @click="onCalculate"
        >
          <font-awesome-icon :icon="['fas', 'calculator']" />
          Calculate
        </b-button>
        <b-button
          v-if="canChangePermission(table.schema)"
          id="updatePermissions"
          size="sm"
          variant="outline-secondary"
          :to="{name: 'dataManagementPermissions', params: {itemId: $route.params.tableId, type: permissionItemTypes.table}}"
        >
          <font-awesome-icon :icon="['fas', 'key']" />
          Permissions
        </b-button>
        <b-button
          v-if="!isLoanCalculatorOutput && canEdit()"
          id="editTable"
          size="sm"
          variant="outline-primary"
          :to="{
            name: 'dataManagementBuilder.update',
            params: {tableUuid: $route.params.tableId},
            query: {moduleUuid:$route.query.moduleUuid, view:$route.query.view},
          }"
        >
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
          Edit Table
        </b-button>
        <b-button
          v-if="isLoanCalculatorOutput"
          size="sm"
          variant="outline-primary"
          @click="onCalculatorConfig"
        >
          <font-awesome-icon :icon="['fas', 'calculator']" />
          Edit Configuration
        </b-button>
      </div>
    </div>


    <div
      id="pageContent"
      class="page-content d-flex flex-column p-3"
    >
      <content-spinner
        :show="loading"
        text="Please wait..."
      />
      <div
        v-if="!loading"
        class="complex-table-wrapper"
      >
        <complex-table
          ref="table"
          :small="true"
          :group-by="groupBy"
          :sub-group-by="subGroupBy"
          :show-filters="showFilters"
          :sorting="sorting"
          :fields="tableFields"
          :items="tableItems"
          :read-only="readOnly"
          :sortable="sortable"
          :total-rows="totalRows"
          :all-field="fieldsForImportAndExport"
          :loading="table.loading"
          :is-loan-calculator-output="isLoanCalculatorOutput"
          :paged-data-params="getParams()"
          :loading-cell="loadingCell"
          @changed-row="changedRow"
          @add-new-entity="addNewEntity"
          @cell-edit-finish="onCellEdit"
          @on-edit="onEdit"
          @on-confirm="onConfirm"
          @on-add-new-relational-entity="onAddNewRelationalEntity"
          @on-export="exportData"
          @on-delete="onDelete"
          @on-history="onHistory"
          @sort-changed="onSortChanged"
          @group-by-changed="onGroupByChanged"
          @on-freeze-columns-change="onFreezeColumnsChange"
          @filter-changed="onFilterChanged"
          @limited-height-changed="onLimitedHeightChange"
          @filter-primary-key="filterByPrimaryKey"
          @on-pagination-change="onPaginationChange"
          @display-date-change="onDisplayDateChange"
          @on-bulk-delete="onBulkDelete"
          @chooseView="chooseView"
        />
      </div>
    </div>

    <AddEditCalculatorModal />

    <UploadHistory 
      v-if="showUploadHistoryModal"
      :table-ids="[this.$route.params.tableId]"
      @close="showUploadHistoryModal = false" 
    />
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import AddNewRow from './modals/AddNewRow';
import ComplexTable from '../../../../core/components/complex-table/ComplexTable';
import filterMixin from '../../../../core/mixins/filter.mixin';
import Breadcrumb from '../../../../core/components/Breadcrumb';
import moment from 'moment';
import * as XLSX from 'sheetjs-style';
import * as FileSaver from 'file-saver';
import AddEditCalculatorModal from '../../../LoanCalculator/components/AddEditCalculatorModal.vue';
import {RESET_MDM_VIEWS, SELECT_MDM_VIEW} from '../../../../store/modules/data-management/mutation-types';
import ToggleColumnsButton from './ToggleColumnsButton.vue';
import EntityViewsDropdown from './EntityViewsDropdown.vue';
import {mergeArrays} from '../../../../core/utils/index';
import UploadHistory from './modals/UploadHistory.vue';

const {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} = createNamespacedHelpers('dataManagement');

const {
  mapState: mapStateLoanCalculator,
  mapActions: mapActionsLoanCalculator,
} = createNamespacedHelpers('loanCalculator');

const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');

export default {
  name: 'TableView',
  components: {
    EntityViewsDropdown,
    ToggleColumnsButton,
    AddEditCalculatorModal,
    ComplexTable,
    AddNewRow,
    ContentSpinner,
    Breadcrumb,
    UploadHistory,
  },
  mixins: [filterMixin],
  data: () => {
    return {
      items: [],
      localFields: [],
      defaultStyles: null,
      isRowHeightChanged: false,
      readOnly: false,
      sortable: true,
      groupBy: null,
      groupByGuid: null,
      subGroupBy: null,
      subGroupByGuid: null,
      filters: {},
      showFilters: false,
      sortBy: null,
      sortDesc: null,
      sorting: [],
      sortedColumns: {},
      loading: false,
      loanCalculatorOutputSortFields: ['FundName', 'Instrument', 'InvestorName', 'StartDate'],
      params: {
        sortCriteria: [],
        filters: {},
        styles: {},
      },
      filterValues: {
        '=': 'equals',
        '!=': 'doesNotEqual',
        '<': 'lessThan',
        '<=': 'lessThanOrEqualTo',
        '>': 'greaterThan',
        '>=': 'greaterThanOrEqualTo',
        '*': 'contains',
        '^': 'startsWith',
        '$': 'endsWith',
        '!*': 'doesNotContain',
      },
      loadingCell: {row: -1, column: null},
      stickyColumns: [],
      columnsChanged: false,
      showUploadHistoryModal: false,
    };
  },
  // commented out as sorting is done on backend now - in case we ever switch this back, just to know what the idea was
  // watch: {
  //   isLoanCalculatorOutput: {
  //     immediate: true,
  //     async handler() {
  //       // if (this.isLoanCalculatorOutput) {
  //         this.prepareLoanCalculatorFieldSorting();
  //         await this.getAllCalculator();
  //         await this.getData();
  //       //}
  //     },
  //   },
  // },
  computed: {
    ...mapState(['table', 'permissionTypes', 'validation']),
    ...mapGetters(['fields']),
    // ...mapGetters({
    //   selectedView: 'selectedMDMView'
    // }),
    selectedView() {
      return this.table.views.find(el => el.isSelected) || {viewGuid: ''};
    },
    ...mapState({
      permissionItemTypes: state => state.permissionItemTypes,
      tableData: state => state.tableData,
      tables: state => state.tables.data,
      allTableFields: state => state.fields.data,
    }),
    ...mapStateLoanCalculator({
      loanCalculatorData: state => state.data,
    }),
    tableAllFields() {
      const fields = this.localFields
        .map(f => {
          const jsonData = typeof f.dataTypeJSON === 'string' ? JSON.parse(f.dataTypeJSON) : f.dataTypeJSON;
          if (f.dataTypeName === this.fieldValues.parentChildHierarchy && jsonData.ControlType === 'tree') {
            const primaryField = f.data.options.hierarchyField.primary;
            const displayField = f.data.options.hierarchyField.display;

            f = {...f, ...this.buildTree(f.data.options.items, f)};

            f.config = function (node) {
              return {
                id: node[primaryField.name],
                label: node[displayField.name],
                children: node.children,
              };
            };
          }
          return f;
        })
        .filter(f => {
          if ([this.permissionTypes.none, this.permissionTypes.deny].includes(f.currentUserPermissionLevel)) {
            return false;
          }

          let show = f.displayInTable && !f.isPrimaryKey;

          if (!this.readOnly) {
            show = show && !f.isInternal;
          }

          return show;
        });
      if (!this.table.schema.toggleColumns) {
        return fields;
      }

      const uuidToPositionMap = new Map();
      this.table.schema.toggleColumns.forEach((obj) => {
        uuidToPositionMap.set(obj.guid, obj.position);
      });

      return fields
        .sort((f1, f2) => {
          const positionA = uuidToPositionMap.get(f1.fieldGuid);
          const positionB = uuidToPositionMap.get(f2.fieldGuid);

          if (positionA < positionB) {
            return -1;
          } else if (positionA > positionB) {
            return 1;
          } else {
            return 0;
          }
        });
    },
    tableFields() {
      if (!this.table.schema.toggleColumns) {
        return this.tableAllFields;
      }
      const uuidToCheckedMap = new Map();
      this.table.schema.toggleColumns.forEach((obj) => {
        uuidToCheckedMap.set(obj.guid, obj.checked);
      });
      return this.tableAllFields.filter(field => !!uuidToCheckedMap.get(field.fieldGuid));
    },
    toggleColumns() {
      return this.table.schema.toggleColumns;
    },
    fieldsForImportAndExport() {
      const primaryField = [];
      this.localFields = this.fields;

      const fields = this.localFields.reduce((fields, field) => {
        if (field.isPrimaryKey) {
          primaryField.push(field);
        }

        if (!field.isInternal && !field.isPrimaryKey) {
          fields.push(field);
        }
        return fields;
      }, []);

      return [...fields, ...primaryField];
    },
    tableItems() {
      const items = this.items.map(item => {
        let result = {};
        const lookupFields = this.localFields.filter(f => f.dataTypeName === this.fieldValues.lookup);
        if (lookupFields.length > 0) {
          lookupFields.forEach(f => {
            const suggestName = item[`${f.name}__$LookupStatus`];
            if (suggestName) {
              const suggestions = suggestName.Suggestions || suggestName.suggestions;
              if (suggestions && suggestions.length > 0) {
                suggestName.options = [];
                suggestName.options.push({
                  id: 'other',
                  text: 'Other Values',
                  $isDisabled: true,
                });
                suggestions.sort(function (a, b) {
                  const aValue = a.Confidence || a.confidence;
                  const bValue = b.Confidence || b.confidence;
                  return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                });
                suggestName.options = [...suggestName.options, ...f.data.options];
                suggestions.forEach(suggestItem => {
                  const recordGuid = suggestItem.RecordGuid || suggestItem.recordGuid;
                  const suggestedValue = suggestItem.SuggestedValue || suggestItem.suggestedValue;
                  const percentage = suggestItem.Confidence || suggestItem.confidence;
                  const index = suggestName.options.findIndex(data => data.id === recordGuid);
                  if (index > -1) {
                    suggestName.options.splice(index, 1);
                  }
                  suggestName.options.unshift({
                    id: recordGuid,
                    text: suggestedValue,
                    backgroundColor: this.computedColor(percentage),
                  });
                });
                suggestName.options.unshift({
                  id: 'suggested',
                  text: 'Suggested Values',
                  $isDisabled: true,
                });
                result = item[f.name] === null ? {...item, customClass: true} : item;
              } else if (!result.customClass) {
                result = item;
              }
            } else if (!result.customClass) {
              result = item;
            }
          });
        } else {
          result = item;
        }
        /*if (item[this.validation.status] === this.validation.validationFailed && !result._rowVariant) {
          result = {...result, _rowVariant: 'failed'};
        }
        if (item[this.validation.status] === this.validation.validated && !result._rowVariant) {
          result = {...result, _rowVariant: 'validated'};
        }*/
        return result;
      });

      return items;
    },
    checkedItemsExist() {
      return this.table.checkedItems.length;
    },
    isEditViewButtonHighlighted() {
      return this.filtersApplied
          || this.sortingApplied
          || this.groupingApplied
          || this.freezingApplied
          || this.columnsChanged
          || this.isRowHeightChanged;
    },
    filtersApplied() {
      return Object.keys(this.params.filters).some(fieldType => this.params.filters[fieldType].length);
    },
    filtersAppliedCount() {
      return Object.keys(this.params.filters).filter(fieldType => this.params.filters[fieldType].length).length +
          this.params.sortCriteria.length + (this.groupBy ? 1 : 0);
    },
    sortingApplied() {
      return !!this.sortBy;
    },
    groupingApplied() {
      return !!this.groupBy;
    },
    freezingApplied() {
      return !!this.stickyColumns.filter(c => c !== 'selectColumn').length;
    },
    isLoanCalculatorOutput() {
      return !!this.getCurrentLoanCalculator();
      // return (!!Object.keys(this.tableData.data).length && this.tableData.data.name.indexOf('OutputLoanCalculator') > -1);
    },
    table_enabledBulkActions() {
      return this.table.checkedItems.length || this.filtersApplied;
      // return this.$refs.table && this.$refs.table.enabledBulkActions
    },
    table_canEdit() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ].includes(this.table.schema.currentUserPermissionLevel);
    },
    table_canEditField() {
      // allow bulk edit if user has write or higher permissions on any field
      const editPermissions = [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ];
      return this.table.schema.fields.some(f => editPermissions.includes(f.currentUserPermissionLevel));
    },
    table_canDelete() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ].includes(this.table.schema.currentUserPermissionLevel);
    },
    totalRows() {
      const total = this.table.pagination.total;
      return total ? total : 0;
    },
  },
  methods: {
    ...mapActions(['getTable', 'deleteTableRecord', 'showAddNewRecordModal', 'addNewRecord',
      'changePaginationData', 'getInputRelatedColumnOptions', 'deleteTable', 'exportSchema',
      'showViewRecordModal', 'addItem', 'unsetUncheckedItem', 'deleteTableRecords', 'getTableFields', 'getFields',
      'getPagedData', 'getTableData', 'updatePaginationParams', 'updateTableItems', 'bulkDeleteTableRecords',
      'saveToggleColumns', 'closeContextMenu',
    ]),
    ...mapActionsLoanCalculator(['getAllCalculator', 'showAddEditModal', 'calculate']),
    ...designerMapActions(['setEditRowSectionsModalState']),
    ...mapMutations([SELECT_MDM_VIEW]),
    mergeArrays,
    computedColor(percentage, hue0 = 0, hue1 = 153) {
      const hue = (percentage * (hue1 - hue0)) + hue0;
      return percentage < 0.8 ? 'hsl(' + hue + ', 90%, 80%)' : percentage < 0.9 ? 'hsl(' + hue + ', 48%, 60%)' : 'hsl(' + hue + ', 50%, 50%)';
    },
    async addNewRow() {
      await this.setEditRowSectionsModalState({
        show: true,
        isEditMode: false,
        item: null,
      });
      // let params = {
      //   inline: false,
      //   todo: false,
      //   record: {},
      // };
      // this.showAddNewRecordModal(params);
    },
    isValidSuggestions(suggestionName) {
      const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
      return suggestionName && suggestions && suggestions.length > 0;
    },
    async onAddNewRelationalEntity(item, index, field) {
      const primary = this.localFields.find((f) => f.isPrimaryKey);
      const params = {
        inline: true,
        record: {},
        todo: false,
        relationalTableId: field.relationalTableId,
        rowIndex: index,
        field: field,
        recordUuid: item[primary.name],
        entityUuid: this.$route.params.tableId,
        defaultLookupValue: this.getLookupDefaultValue(item, field, true),
      };
      this.showAddNewRecordModal(params);
    },
    async onEdit(item, index, field) {
      await this.setEditRowSectionsModalState({
        show: true,
        isEditMode: true,
        item: JSON.parse(JSON.stringify(item)),
      });
      // let lookupField = this.localFields.find(f => f.dataTypeName === this.fieldValues.lookup)
      // let params = {
      //   inline: false,
      //   todo: false,
      //   record: item,
      //   defaultLookupValue: this.getLookupDefaultValue(item, lookupField),
      // };
      // this.showAddNewRecordModal(params);
    },
    getLookupDefaultValue(item, field, isRelational = false) {
      if (field !== undefined) {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        let lookupDefaultValue;
        const relationalField = field.data.lookup;
        if (item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          lookupDefaultValue = suggestions[0].OriginalValue || suggestions[0].originalValue;
        }
        return isRelational ? {[relationalField.value]: lookupDefaultValue} : {[field.name]: lookupDefaultValue};
      }
    },
    async onConfirm(item, index, field) {
      const success = await this.$confirm(
        'Confirmed Row',
        'Would you like to change \'Validated\' row to \'Confirmed\'?',
      );
      if (success) {
        await this.onCellEdit(null, null, index, item, field, true);
        this.closeContextMenu();
      }
    },
    isFieldDate(field) {
      return field.dataTypeName === this.fieldValues.date;
    },
    isFieldDateTime(field) {
      return field.dataTypeName === this.fieldValues.dateTime;
    },
    isDateOrDateTime(field) {
      return this.isFieldDate(field) || field.dataTypeName === this.fieldValues.dateTime;
    },
    isFieldLookup(field) {
      return field.dataTypeName === this.fieldValues.lookup;
    },
    isPostedStatus(field) {
      return this.table.schema.postedStatusGuid === field.fieldGuid;
    },
    isLineBreakTextField(field) {
      const dataTypeJSON = JSON.parse(field.dataTypeJSON);
      return dataTypeJSON.IsLineBreak;
    },
    getDateFormat(field) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';
      return `${format}${this.isFieldDate(field) ? '' : ' HH:mm:ss'}`;
    },
    getFormattedDate(field, content) {
      const dateFormat = this.getDateFormat(field);
      const defaultFormat = 'YYYY-MM-DD';
      return this.isFieldDate(field) ?
        this.$options.filters.toDate(moment(content, dateFormat).toDate(), defaultFormat) :
        this.$options.filters.toDatetime(moment(content, dateFormat).toDate(), `${defaultFormat} HH:mm:ss`);
    },
    async onCellEdit(content, oldContent, index, item, field, isConfirm = false) {

      if (this.isFieldLookup(field) && content.id === oldContent.id) {
        return true;
      } else {
        if (
          typeof content !== 'object'
            && content
            && oldContent
            && (content.toString() === oldContent.toString() || this.isDateOrDateTime(field)
                && this.getFormattedDate(field, content) === this.getFormattedDate(field, oldContent))
        ) {
          return true;
        }
      }

      this.loadingCell = {
        row: index,
        column: field.name,
      };
      // this.loading = true
      const data = {...{}, ...item};
      const primaryKey = this.table.schema.fields.find(f => f.isPrimaryKey).name;

      const params = {
        data: [this.formatPostData(data)],
        tableId: this.$route.params.tableId,
        primaryKey,
      };

      const lastUpdateDateName = this.table.schema.fields.find(f => f.dataTypeName === 'lastUpdatedDate').name;
      params.data[0]['UploadAsAtDate'] = item[lastUpdateDateName];

      const validate = this.validation;
      if (item[validate.status] === validate.validated && isConfirm) {
        params.data[0][validate.fieldName] = validate.confirmed;
      }

      const lookupFields = this.table.schema.fields.filter(f => f.dataTypeName === this.fieldValues.lookup);

      if (lookupFields.length > 0) {
        lookupFields.forEach(f => {
          const lookupName = `${f.name}__$LookupStatus`;
          if (item[lookupName]) {
            if (item[lookupName].options) {
              delete item[lookupName].options;
            }
            if (content && content.id === null && item[lookupName].Suggestions) {
              item[lookupName].Suggestions = [];
            }
            params.data[0][lookupName] = item[lookupName];
          }
        });
      }

      if (field.dataTypeName === this.fieldValues.text && item[field.name]) {
        const textItemValue = this.isLineBreakTextField(field)
          ? item[field.name]
          : item[field.name].replace(/\s\s+/g, ' ');
        params.data[0][field.name] = textItemValue.trim();
      }

      // console.log('changed', params);
      const {success, body, updatedRecord} = await this.addNewRecord(params);
      if (success) {
        if (body.status === 'failure' && body.errors) {
          let errorMessage = 'Unable to update record';

          const errorMessagesArray = body.errors.map(error => {
            try {
              return JSON.parse(error).Message;
            } catch (err) {
            }
            return error;
          });

          errorMessagesArray.forEach(error => errorMessage += `\n ${error}`);

          this.$toasted.error(errorMessage, {
            duration: TOASTER_DURATION,
          });
        } else {
          const resData = body.resultSetTemp[0];
          if (resData) {
            if (resData['uploadStatus'] === 'rejected') {
              this.$toasted.error(resData['validationErrors'].map(error => error).join(' '), {
                duration: TOASTER_DURATION,
              });

              if (this.isFieldLookup(field)) {
                item[field.name] = oldContent.id;
              } else {
                item[field.name] = oldContent;
              }
            } else {
              /*let recordLastStatus = resData.updatedRecord[validate.status]
              if ((item[validate.status] === validate.validationFailed ||
                  item[validate.status] === validate.confirmed) &&
                  recordLastStatus === validate.validated) {
                const success = await this.$confirm('Confirmed Row', `Would you like to change 'Validated' row to 'Confirmed'?`)
                if (success) {
                  await this.onCellEdit(content, oldContent, index, resData.updatedRecord, field, true)
                }
              }*/
              this.updateTableItems({
                primaryKey,
                updatedItems: [updatedRecord],
              });
              this.$toasted.success('Record saved successfully', {
                duration: TOASTER_DURATION,
              });
              this.table.checkedAll = false;
              this.mapTableNewSuggestions(updatedRecord);
            }
          }
        }
      } else {
        this.$toasted.error('Unable to update record', {
          duration: TOASTER_DURATION,
        });
      }
      this.loadingCell = {row: -1, column: null};
      this.items = this.sortedTableData();
      // this.loading = false
    },
    onHistory(item) {
      const primary = this.table.schema.fields.find(f => f.dataTypeName === this.fieldValues.primaryKey);
      const params = {
        entityGuid: this.$route.params.tableId,
        recordGuid: item[primary.name],
      };

      this.showViewRecordModal(params);
    },
    formatPostData(item) {
      const fieldMap = {};

      //Since we get data by fields name and API expects displayName convert params
      this.table.schema.fields.forEach((field) => {
        fieldMap[field.name] = {
          name: field.name,
          isInternal: field.isInternal,
          isPrimaryKey: field.isPrimaryKey,
          isSuggested: false,
          permission: field.currentUserPermissionLevel,
        };
      });

      const params = {};
      for (const name in item) {
        const field = fieldMap[name];

        if (field === undefined) {
          delete item[name];
          continue;
        } else if (field.isInternal && !field.isPrimaryKey) { //Skip auto populated columns since it throws error if it's passed
          continue;
        } else if (['read', 'none', 'deny'].includes(field.permission)) {
          continue;
        }
        params[name] = item[name];
      }

      return params;
    },
    async onDelete(item, index, field) {
      const success = await this.$confirm(
        'Deleting Record...',
        'You are deleting record. Are you sure you want to continue?',
      );
      if (success) {
        const primaryKey = this.table.schema.fields.filter(f => f.isPrimaryKey)[0].name;
        const params = {
          recordId: item[primaryKey],
          entityId: this.$route.params.tableId,
        };

        const response = await this.deleteTableRecord(params);
        const { IsError, ErrorMessage } = response.body;

        if (!IsError) {
          this.$toasted.success(
            'Deleted successfully',
            {duration: TOASTER_DURATION},
          );

          this.table.checkedItems = this.table.checkedItems.filter(i => i[primaryKey] !== item[primaryKey]);
        } else {          
          this.$toasted.error(ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
        this.getTableItems();
      }
      this.closeContextMenu();
    },
    async onSortChanged(sorting, afterRemoveParticular = false) {

      this.sorting = this.sorting.map(sort => ({
        ...sort,
        FieldNameOrGuid: sort.FieldNameOrGuid,
      }));
      const mappedSorting = sorting.map(sort => {
        return {
          ...sort,
          FieldNameOrGuid: sort.FieldNameOrGuid,
        };
      },
      );

      if (mappedSorting.length === 0) {
        this.sortBy = null;
        this.sortDesc = null;
        this.sorting = [];
      } else if (!afterRemoveParticular) {
        for (const key of mappedSorting) {
          this.sortBy = key.FieldNameOrGuid;
          this.sortDesc = !key.ascending;
        }

        const existingIdx = this.sorting.findIndex(item => item.FieldNameOrGuid === this.sortBy);

        if (existingIdx !== -1) {
          if (this.sorting[existingIdx].ascending !== !this.sortDesc) {
            this.sorting[existingIdx].ascending = !this.sortDesc;
          }
          if (this.sortDesc === null) {
            this.sorting = this.sorting.filter(item => item.FieldNameOrGuid !== this.sortBy);
          }
        } else {
          this.sorting.push({FieldNameOrGuid: this.sortBy, ascending: !this.sortDesc});
        }
      } else {
        for (const key of mappedSorting) {
          this.sortBy = key.FieldNameOrGuid;
          this.sortDesc = !key.ascending;
        }
        this.sorting = [...mappedSorting];
      }

      await this.applySorting();

      if (!this.filtersApplied && !this.sortingApplied && !this.groupingApplied) {
        this.showFilters = false;
      }
    },
    async onGroupByChanged({groupBy, isGrouped}) {

      const currentField = this.fields.find(el => el.name === groupBy);
      // console.log(this.)
      // If it was grouped
      if (isGrouped) {

        // If it is not grouped by anything, group it
        if (!this.groupBy) {
          this.groupBy = groupBy;
          this.groupByGuid = currentField.fieldGuid;
          // this.sortBy = groupBy
        } else {
          // If it was already grouped, group it as subgroup
          this.subGroupBy = groupBy;
          this.subGroupByGuid = currentField.fieldGuid;
        }
      } else {
        // If grouping was removed
        if (groupBy === this.groupBy) {
          this.groupBy = null;
          this.subGroupBy = null;
        } else if (groupBy === this.subGroupBy) {
          this.subGroupBy = null;
        }
      }

      await this.applySorting();

      if (!this.filtersApplied && !this.sortingApplied && !this.groupingApplied) {
        this.showFilters = false;
      }
    },
    getCurrentField(isSubGroup = false) {
      const currentKey = isSubGroup ? 'subGroupBy' : 'groupBy';
      const sortField = this.localFields.find(el => el.name === this[currentKey]) || {fieldGuid: this.validation.status};
      const asc = this.sortBy === this[currentKey]
        ? !this.sortDesc
        : true;
      this.params.sortCriteria = [
        ...this.params.sortCriteria,
        {FieldNameOrGuid: sortField.fieldGuid, ascending: asc},
      ];
    },
    async applySorting() {
      this.params.sortCriteria = [];
      this.sortedColumns[this.sortBy] = {sortDesc: this.sortDesc};

      this.groupBy && this.getCurrentField();
      this.subGroupBy && this.getCurrentField(true);

      this.params.sortCriteria = [...this.mergeArrays(this.params.sortCriteria, this.sorting)];
      await this.getTableItems();
    },
    async changedRow(insertItem) {
      const {TotalRecords} = await this.getTableItems();

      this.updatePaginationParams({
        total: TotalRecords,
        perPage: this.table.pagination.perPage,
        currentPage: 1,
      });

      this.mapTableNewSuggestions(insertItem);
      this.items = this.sortedTableData();
    },
    mapTableNewSuggestions(item) {
      this.table.schema.fields.map(field => {
        const key = field.name;
        if (field.frontType === 'search-input' && !field.suggestions[key].includes(item[key])) {
          field.suggestions[key].push(item[key]);
        }
        return field;
      });
    },
    addNewEntity(newField, newOption) {
      this.localFields = this.fields.map(field => {
        if (field.name === newField.name) {
          field.data.options.splice(1, 0, newOption);
        }
        return field;
      });
    },
    async onFilterChanged(filter) {
      const {isBackFilter, filterFieldName} = this.setFilteredParams(filter);

      if (isBackFilter) {
        const {TotalRecords} = await this.getTableItems();

        this.updatePaginationParams({
          total: TotalRecords,
          perPage: this.table.pagination.perPage,
          currentPage: 1,
        });
      } else {
        this.items = this.table.data.filter((item) => {
          if (filterFieldName === this.fieldValues.selectColumn && !this.dateFilter(filter[filterFieldName], item)) {
            return false;
          } else if (filterFieldName === 'recordStatus' && !this.statusFilter(filter[filterFieldName], item[this.validation.status])) {
            return false;
          }
          return true;
        });
      }
      if (!this.filtersApplied && !this.sortingApplied && !this.groupingApplied) {
        this.showFilters = false;
      }
    },
    setFilteredParams(filter) {
      this.params.filters = {
        lookupFilters: [],
        textFilters: [],
        numericFilters: [],
        dateFilters: [],
        boolFilters: [],
      };

      for (const filterFieldName in filter) {
        if (!filter.hasOwnProperty(filterFieldName)) {
          continue;
        }

        if ([this.fieldValues.selectColumn].includes(filterFieldName)) {
          return {isBackFilter: false, filterFieldName: filterFieldName};
        }

        let field = {};
        let primaryFieldName;
        this.fields.forEach((f) => {
          if (f.name === filterFieldName) {
            field = f;
          }
          if (f.isPrimaryKey) {
            primaryFieldName = f.name;
          }
        });
        const filterData = filter[filterFieldName];
        const fieldGuid = field.fieldGuid;
        if (field.dataTypeName === this.fieldValues.text && !(field.key === 'PostedStatus')) {
          this.setTextNumberFilters('textFilters', filterData, fieldGuid);
        } else if ([
          this.fieldValues.wholeNumber,
          this.fieldValues.decimalNumber,
          this.fieldValues.floatNumber,
        ].includes(field.dataTypeName)
        ) {
          this.setTextNumberFilters('numericFilters', filterData, fieldGuid);
        } else if (field.dataTypeName === this.fieldValues.boolean) {
          this.setBooleanFilters('boolFilters', filterData, fieldGuid);
        } else if (field.dataTypeName === this.fieldValues.lookup || field.dataTypeName === this.fieldValues.parentChildHierarchy) {
          this.setLookupFilters('lookupFilters', filterData, fieldGuid, 'id');
          // } else if (field.dataTypeName === this.fieldValues.parentChildHierarchy) {
          //   this.setLookupFilters('lookupFilters', filterData, fieldGuid, primaryFieldName);
        } else if ([this.fieldValues.dateTime, this.fieldValues.date].includes(field.dataTypeName)) {
          this.setDateFilters('dateFilters', filterData, fieldGuid, field);
        } else if (filterFieldName === 'recordStatus') {
          this.setRecordStatusFilter(filterData);
        } else if (field.key === 'PostedStatus') {
          this.setPostedStatusFilters(filterData.selectedPostedStatuses);
        }
      }
      return {isBackFilter: true, filterFieldName: null};
    },
    setRecordStatusFilter(filterData) {
      this.params.filters = {
        ...this.params.filters,
        recordStatusFilters: filterData.selectedStatus,
      };
    },
    setTextNumberFilters(type, filterData, fieldGuid) {
      const operator = this.filterValues[filterData.type];
      const value = filterData.text;

      this.params.filters[type].push({
        fieldGuid,
        filterOperator: operator,
        value: value,
      });
    },
    setBooleanFilters(type, filterData, fieldGuid) {
      const value = filterData.selected;
      const index = this.params.filters[type].findIndex(f => f.fieldGuid === fieldGuid);

      if (value == -1) {
        if (index !== -1) {
          delete this.params.filters[type][index];
        }
        return;
      }

      this.params.filters[type].push({
        fieldGuid,
        istrue: value === '1',
      });
    },
    setLookupFilters(type, filterData, fieldGuid, primaryFieldName) {
      this.params.filters[type].push({
        fieldGuid,
        lookupValues: filterData.selectItems.map(i => i[primaryFieldName]),
      });
    },
    setDateFilters(type, filterData, fieldGuid, field) {
      const defaultFormat = 'YYYY-MM-DD';
      const parseFormat = `${defaultFormat}${this.isFieldDateTime(field) ? ' HH:mm:ss' : ''}`;

      this.params.filters[type].push({
        fieldGuid,
        fromDate: moment(filterData.fromDate, parseFormat).format(`${defaultFormat}[T]HH:mm:ss.SSS[Z]`),
        toDate: moment(filterData.toDate, parseFormat).format(`${defaultFormat}[T]HH:mm:ss.SSS[Z]`),
      });
    },
    setPostedStatusFilters(selectedPostedStatuses) {
      this.params.filters.postedStatusFilters = selectedPostedStatuses;
    },
    getFiltersFromUrl() {
      if (this.$route.query.filters) {
        this.setFilteredParams(JSON.parse(decodeURIComponent(this.$route.query.filters)));
      }
      /*if (this.$route.query.primaryKey) {
        let params = {
          [this.table.schema.fields.filter(f => f.isPrimaryKey)[0].name]: JSON.parse(this.$route.query.primaryKey),
        }
        this.filterByPrimaryKey(params);
      }*/
    },
    filterByPrimaryKey(filter) {
      const fieldName = this.table.schema.fields.filter(f => f.isPrimaryKey)[0].name;
      this.items = this.items.filter((item) => {
        return filter[fieldName].includes(item[fieldName]);
      });
    },
    getInternalFieldValue(uploadField, fieldToGet) {
      return uploadField[Object.keys(uploadField).reduce((fieldName, field) => {
        if (field.toLowerCase().includes(fieldToGet.toLowerCase())) {
          fieldName = field;
        }
        return fieldName;
      }, '')];
    },
    dateFilter(filteredField, uploadField) {
      if (uploadField) {
        const format = date => moment(date).format('YYYY-MM-DD');
        const dateFormat = format(uploadField);

        if (filteredField.fromDate && filteredField.toDate) {
          if (!(filteredField.fromDate <= dateFormat && filteredField.toDate >= dateFormat)) {
            return false;
          }
        } else if (filteredField.fromDate) {
          if (!(filteredField.fromDate <= dateFormat)) {
            return false;
          }
        } else if (filteredField.toDate) {
          if (!(filteredField.toDate >= dateFormat)) {
            return false;
          }
        } else if (filteredField.createdDate || filteredField.updatedDate) {
          const isCreatedDate = filteredField.createdDate;
          const isUpdatedDate = filteredField.updatedDate;
          const filterType = isCreatedDate && isUpdatedDate ? 'both' : isCreatedDate ? 'createdDate' : 'updatedDate';

          const createdDateEqualsToUploadedValue = format(this.getInternalFieldValue(uploadField, 'createdDate')) === isCreatedDate;
          const updatedDateEqualsToUploadedValue = format(this.getInternalFieldValue(uploadField, 'lastUpdatedDate')) === isUpdatedDate;

          if (filterType === 'both' && !(createdDateEqualsToUploadedValue && updatedDateEqualsToUploadedValue)) {
            return false;
          } else if (filterType === 'createdDate' && !(createdDateEqualsToUploadedValue)) {
            return false;
          } else if (filterType === 'updatedDate' && !(updatedDateEqualsToUploadedValue)) {
            return false;
          }
        }
        return true;
      }
      return false;
    },
    statusFilter(filteredField, uploadField) {
      const status = filteredField['selectedStatus'];
      if (status.length > 0) {
        return status.includes(uploadField);
      }
    },
    async onPaginationChange(params) {
      await this.unsetUncheckedItem({type: 'all'});
      await this.changePaginationData(params);
      await this.getTableItems();
      // setTimeout(async () => await this.$refs.table.addFirstFiftyOptions(), 0);
      this.localFields = this.fields;
    },
    onDisplayDateChange(event) {
      this.readOnly = event !== moment().format('YYYY-MM-DD');
    },
    getParams() {
      const {
        currentPage: pageNumber,
        perPage: pageSize,
      } = this.table.pagination;

      const params = JSON.parse(JSON.stringify(this.params));

      if (Array.isArray(params.filters.lookupFilters)) {
        params.filters.lookupFilters = params.filters.lookupFilters.filter(item => item.lookupValues.length);
      }

      return {
        ...params,
        id: this.$route.params.tableId,
        pageNumber,
        singleRecordGuid: this.$route.query.PrimaryKey,
        pageSize,
        groupBy: this.groupByGuid,
        subGroupBy: this.subGroupByGuid,
        viewGuid: this.$route.query.view,
      };
    },
    // getFilters() {
    //   const filters = {}
    //
    //   for (let filterFieldName in this.params.filters) {
    //
    //     this.params.filters[filterFieldName].forEach(filter => {
    //       const field = this.fields.find(f => f.fieldGuid === filter.fieldGuid)
    //
    //       if (filterFieldName === 'lookupFilters') {
    //         console.log("lookupFilters")
    //         console.log(field)
    //       } else if (filterFieldName === 'textFilters') {
    //           if (field) {
    //             filters[field.name] = {
    //               fieldType: field.dataTypeName,
    //               label: field.displayName,
    //               text: filter.value,
    //               type: this.filterOptionValues[filter.filterOperator],
    //             }
    //           }
    //       } else if (filterFieldName === 'numericFilters') {
    //
    //       } else if (filterFieldName === 'dateFilters') {
    //
    //       } else if (filterFieldName === 'boolFilters') {
    //
    //       }
    //     })
    //   }
    //   console.log("===========================")
    //   console.log("===========================")
    //   console.log(filters)
    //
    //   return filters;
    // },
    async getTableItems() {
      const {TotalRecords} = await this.getPagedData(this.getParams());

      this.updatePaginationParams({
        total: TotalRecords,
        perPage: this.table.pagination.perPage,
        currentPage: 1,
      });
      this.items = this.sortedTableData();

      return {TotalRecords};
    },
    async getData() {
      await this.getTable(this.getParams());
      this.localFields = this.fields;
      this.items = this.sortedTableData();
    },
    sortedTableData() {
      return this.table.data;
      // let primaryField = this.localFields.find(f => f.dataTypeName === this.fieldValues.primaryKey);
      //
      // if (!primaryField || this.params.sortCriteria.length) {
      //   return this.table.data;
      // }
      //
      // return this.table.data.sort(function (a, b) {
      //   return a[primaryField.name] < b[primaryField.name] ? -1 : a[primaryField.name] > b[primaryField.name] ? 1 : 0
      // });
    },
    onLimitedHeightChange() {
      this.isRowHeightChanged = true;
    },
    canAddRow(schema) {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ].includes(schema.currentUserPermissionLevel);
      // return schema.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    canChangePermission(schema) {
      return schema.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    canEdit() {
      // return this.$refs.table && this.$refs.table.canEdit();
      return [
        this.permissionTypes.admin,
        this.permissionTypes.edit,
      ].includes(this.table.schema.currentUserPermissionLevel);
    },
    canDelete() {
      // return this.$refs.table && this.$refs.table.canDelete();
      return [this.permissionTypes.admin].includes(this.table.schema.currentUserPermissionLevel);
    },
    exportData(params) {
      const type = 'text/csv;charset=UTF-8';
      const wscols = [];
      for (let i = 0; i < params.header.length; i++) {  // columns length added
        wscols.push({wch: 25});
      }

      const customFields = params.customFields;
      const worksheet = XLSX.utils.json_to_sheet(params.exportData);

      const lineBreakCells = [];
      Object.keys(worksheet).forEach(cell => {
        if (customFields.lineBreakTextFields[worksheet[cell].v]) {
          lineBreakCells.push(cell.toString()[0]);
        }
      });


      for (const cell in worksheet) {
        if (worksheet.hasOwnProperty(cell)) {
          // Get current column name
          const col = cell.split(/([0-9]+)/)[0];

          // Add cell styles to the required fields
          if (customFields.requiredFields[worksheet[cell].v]) {
            worksheet[cell].s = {
              fill: {
                fgColor: {
                  rgb: 'F5C6CB',
                },
              },
            };
          }

          if (lineBreakCells.includes(cell.toString()[0])) {
            worksheet[cell].s = {
              ...worksheet[cell].s ? worksheet[cell].s : {},
              alignment: {
                wrapText: true,
              },
            };
          }

          // Add cell format for date and number fields
          if (customFields.dateFields[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, 'yyyy/mm/dd');
          }
          if (customFields.dateTimeFields[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, 'yyyy/mm/dd hh:mm:ss');
          }

          if (customFields.wholeNumberFields.separation[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, '#,##0');
          }
          if (customFields.decimalFields.separation[worksheet[cell].v]) {
            const zeroCount = this.displayToDecimal(customFields.decimalFields.separation[worksheet[cell].v]);
            this.getExcelColumns(col, worksheet, this.getDecimalFieldExcelFormat(customFields.decimalFields.separation[worksheet[cell].v], zeroCount));
          }

          if (customFields.wholeNumberFields.nonSeparation[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, '0');
          }
          if (customFields.decimalFields.nonSeparation[worksheet[cell].v]) {
            const zeroCount = this.displayToDecimal(customFields.decimalFields.nonSeparation[worksheet[cell].v]);
            this.getExcelColumns(col, worksheet, `0.${zeroCount}`);
          }
        }
      }
      worksheet['!cols'] = wscols;
      const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
      const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
      const data = new Blob([excelBuffer], {
        type: type,
      });
      FileSaver.saveAs(data, this.table.schema.displayName + '.xlsx');
      this.unsetUncheckedItem({type: 'all'});
    },
    getExcelColumns(col, ws, fmt) {
      const C = XLSX.utils.decode_col(col);
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let i = range.s.r + 1; i <= range.e.r; ++i) {
        const ref = XLSX.utils.encode_cell({r: i, c: C});
        if (!ws[ref]) continue;
        if (ws[ref].t !== 'n') continue;
        ws[ref].z = fmt;
      }
    },
    displayToDecimal(field) {
      const json = JSON.parse(field.dataTypeJSON);
      // return '0'.repeat(json.DecimalsToDisplay)
      return '0'.repeat(json.Scale);
    },
    getDecimalFieldExcelFormat(field, zeroCount) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      return jsonData.IsPercentage ? `0.${zeroCount}%` : `#,##0.${zeroCount}`;
    },
    buildTree(arr, hierarchyField) {
      const levelField = hierarchyField.data.options.hierarchyField.level;
      const primaryField = hierarchyField.data.options.hierarchyField.primary;
      const parentField = hierarchyField.data.options.hierarchyField.parent;

      const jsonData = JSON.parse(hierarchyField.dataTypeJSON);
      const minLevel = jsonData.MinimumLevelSelectable;
      const maxLevel = jsonData.MaximumLevelSelectable;

      const tree = [];
      const treeLevels = {};
      const rawArray = JSON.parse(JSON.stringify(arr)); //make copy of original
      const map = {};

      for (const item of rawArray) {
        map[item[primaryField.name]] = item;
      }

      for (const item of rawArray) {
        if (item[parentField.name]) {
          if (!map[item[parentField.name]].children) {
            map[item[parentField.name]].children = [];
          }
          map[item[parentField.name]].children = [...map[item[parentField.name]].children, item];
        } else {
          tree.push(map[item[primaryField.name]]);
        }
      }
      this.setLevelName(tree, 0, levelField, treeLevels, minLevel, maxLevel);

      return {tree, treeLevels};
    },
    setLevelName(arr, level, levelField, treeLevels, minLevel, maxLevel) {
      if (!arr) {
        return;
      }

      let disabled = false;
      if (minLevel > level) {
        if (maxLevel === null) {
          disabled = true;
        }
        if (maxLevel >= level) {
          disabled = true;
        }
      }
      if (maxLevel !== null && !(minLevel === level || maxLevel >= level)) {
        disabled = true;
      }

      arr.forEach((elem) => {
        treeLevels[level] = elem[levelField.name];
        elem.level = level;
        elem.disabled = disabled;
        this.setLevelName(elem.children, level + 1, levelField, treeLevels, minLevel, maxLevel);
      });
    },
    async onBulkDelete() {
      const success = await this.$confirm('Deleting Records..', 'You are deleting checked records. Are you sure you want to continue?');
      if (success) {
        const primaryKey = this.table.schema.fields.find(f => f.isPrimaryKey).name;

        const params = {
          data: this.table.checkedItems.map(item => item[primaryKey]),
          entityId: this.$route.params.tableId,
        };

        let response;
        let deletedRecordCount;
        if (this.table.data.length && (this.table.data.length === this.table.checkedItems.length) || (this.filtersApplied && !this.table.checkedItems.length)) {
          response = await this.bulkDeleteTableRecords({
            entityId: this.$route.params.tableId,
            filters: this.params.filters,
          });
          deletedRecordCount = parseInt(response.body);
        } else {
          response = await this.deleteTableRecords(params);
          deletedRecordCount = this.table.checkedItems.length;
        }

        const {success, body} = response;
        if (success) {
          this.$toasted.success(`${deletedRecordCount} Record(s) Deleted`, {
            duration: TOASTER_DURATION,
          });
        } else {
          this.$toasted.error(body.ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
        await this.unsetUncheckedItem({type: 'all'});
        await this.getTableItems();
      }
    },
    async beforeRouteAction() {
      if (!this.checkedItemsExist) {
        await this.changePaginationData({perPage: this.table.pagination.perPage, currentPage: 1});
        return true;
      }

      const confirm = await this.$confirm(
        'Redirecting...',
        'You have checked items to bulk actions. Are you sure you want to continue?',
      );
      if (confirm) {
        await this.unsetUncheckedItem({type: 'all'});
        await this.changePaginationData({perPage: this.table.pagination.perPage, currentPage: 1});
        return true;
      } else {
        return false;
      }
    },
    setConfigs() {
      if (this.isLoanCalculatorOutput) {
        this.sortable = false;
        this.readOnly = true;
      }
    },
    prepareLoanCalculatorFieldSorting() {
      this.loanCalculatorOutputSortFields.forEach(fieldName => {
        const field = this.fields.find(f => f.name === fieldName);
        if (field) {
          const fieldGuid = field.fieldGuid;
          this.params.sortCriteria.push({ascending: true, FieldNameOrGuid: fieldGuid});
        }
      });
    },
    async onCalculate() {
      const calculator = this.getCurrentLoanCalculator();
      this.loading = true;
      const {success, body} = await this.calculate(calculator.loanCalculatorId);
      if (success) {
        this.$toasted.success(`Entity with name ${calculator.loanCalculatorName} calculated successfully`, {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }
      this.loading = false;
    },
    // onChooseView({view, makeRequest = true}) {
    //   if (view && view.viewGuid) {
    //     this[SELECT_MDM_VIEW](view.viewGuid)
    //     const query = {...this.$route.query}
    //     delete query.filters
    //     this.$router.push({query: {...query, view: view.viewGuid}}).catch(() => {
    //     })
    //   } else {
    //     this[SELECT_MDM_VIEW](null)
    //     const tmpQuery = {...this.$route.query};
    //     delete tmpQuery.view
    //     this.$router.push({query: tmpQuery}).catch(() => {
    //     })
    //   }
    //
    //   this.chooseView({view, makeRequest})
    // },
    chooseView({view, makeRequest = true}) {
      if (view && view.viewGuid) {
        let params = JSON.parse(view.viewJSON);
        // temporary fix
        try {
          params = JSON.parse(params);
        } catch (e) {
          // console.error(e)
        }

        this.params.filters = params.filters;
        this.params.sortCriteria = params.sortCriteria;
        this.params.pageSize = params.pageSize;
        this.params.styles = params.styles;
        this.defaultStyles = params.styles;

        if (this.params.sortCriteria.length) {
          for (let i = 0; i < params.sortCriteria.length; i++) {
            const sortField = params.sortCriteria[i];
            const field = this.localFields.find(f => f.name === sortField.FieldNameOrGuid);
            if (!field || field.name === params.groupBy || field.name === params.subGroupBy) {
              continue;
            }
            this.sortBy = field.name;
            this.sortDesc = !sortField.ascending;
          }
          this.sorting = [...params.sortCriteria];
        }
        this.groupBy = params.groupBy;
        this.subGroupBy = params.subGroupBy;
        if (makeRequest) {
          this.getTableItems();
        }
      } else {
        this.params.sortCriteria = [];

        this.groupBy = null;
        this.subGroupBy = null;
        this.sortBy = null;
        this.sortDesc = null;
        this.sorting = [];
        if (makeRequest) {
          this.onFilterChanged({});
        }
      }
    },
    onCalculatorConfig() {
      this.showAddEditModal(this.getCurrentLoanCalculator());
    },
    getCurrentLoanCalculator() {
      return this.loanCalculatorData.find(calc => calc.loanCalculatorName.trim() === this.table.schema.displayName.trim());
    },
    table_showImport(arg) {
      return this.$refs.table.showImport(arg);
    },
    table_exportData() {
      return this.$refs.table.exportData();
    },
    table_bulkEditRows() {
      return this.$refs.table.bulkEditRows();
    },
    table_onBulkDelete() {
      return this.$refs.table.onBulkDelete();
    },
    onToggleColumnsChanged(newColumns) {
      this.saveToggleColumns({
        entityGuid: this.$route.params.tableId,
        columns: newColumns.map((field, ind) => ({
          guid: field.fieldGuid,
          checked: field.checked,
          position: ind + 1,
        })),
      }).then(() => {
        this.columnsChanged = true;
      });
    },
    onFreezeColumnsChange(columns) {
      this.stickyColumns = [...columns];
    },
  },
  async mounted() {
    this.loading = true;

    await this.getTableFields(this.$route.params.tableId);
    await this.getFiltersFromUrl();

    // if (this.isLoanCalculatorOutput) {
    //   // this.getData is called from watcher
    // } else {
  await this.getData();
    //}

    this.setConfigs();

    this.loading = false;

    const uuid = this.$route.query.view;
    if (uuid) {
      this[SELECT_MDM_VIEW](uuid);
    }

    this.chooseView({view: this.selectedView, makeRequest: false});
  },
  async beforeRouteUpdate(to, from, next) {
    if (await this.beforeRouteAction()) {
      return next();
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (await this.beforeRouteAction()) {
      return next();
    }
  },
};
</script>

<style lang="scss" scoped>
.actions_wrapper {
  display: flex;
  align-items: center;
  gap: 5px;

  &:has(.btn) {
    white-space: nowrap;
    flex-wrap: nowrap;
  }
}

.page-data-management {
  flex: 1;
  overflow: hidden;
}

.complex-table-wrapper {
  flex: 1;
  overflow: hidden;
}

// Use ::v-deep to pierce through scoped styling and target BootstrapVue dropdown-menu class
::v-deep .dropdown-menu {
  min-width: 11rem !important;
}

// Align icon with text in dropdown menu
.dropdown-item svg {
  position: relative;
  top: 5px; 
}
</style>
